import { useEffect, useState, useRef, FunctionComponent } from "react";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
  returnVatOfPriceOnly,
  returnPriceMinusVat,
  returnVatOfPrice,
} from "../../../core/helpers";
import {
  selectFormArr,
  selectRegistrationDetail,
  selectPlateFixture,
  setPlateFixture,
  setPlateFixtureValue,
  selectPlateFixtureValue,
  selectCalcPrice,
  selectCreditCardMessage,
  setFinancePrice,
  selectDiscountCode,
  selectDiscountAmount,
  selectDiscountAppliesTo,
  clearDiscount,
  setAutoDiscount,
  applyDiscountCode,
} from "../checkoutPaymentSlice";
import { numberWithCommas } from "../../../core/helpers/formatHelper";
import { PRICES } from "../../../prices";

type Props = {
  registration: string;
  isFinance: boolean;
};

const POUND = "\u00A3";

const ORDER_SPECIAL_DELIVERY = PRICES.deliverySpecial;
const ORDER_RECORDED_DELIVERY = PRICES.deliveryRecorded;
const STANDARD_DELIVERY = PRICES.deliveryStandard;
const ORDER_TRANSFER_FEE_STANDARD = PRICES.transferService;

const PriceBreakdown: FunctionComponent<Props> = (props: Props) => {
  const dispatch = useAppDispatch();
  const { registration, isFinance } = props;

  const plateFixture = useAppSelector(selectPlateFixture);
  const plateFixtureValue = useAppSelector(selectPlateFixtureValue);

  const registrationDetail = useAppSelector(selectRegistrationDetail);
  const formArr = useAppSelector(selectFormArr);
  const calcPrice = useAppSelector(selectCalcPrice);
  const creditCardMessage = useAppSelector(selectCreditCardMessage);
  const discountCode      = useAppSelector(selectDiscountCode);
  const discountAmount    = useAppSelector(selectDiscountAmount);
  const discountAppliesTo = useAppSelector(selectDiscountAppliesTo);

  const discountLabel: Record<string, string> = {
    total:            "Discount applied",
    transfer_fee:     "Transfer fee discount applied",
    plates:           "Plates discount applied",
    transfer_service: "Transfer service discount applied",
  };
  const appliedLabel = discountLabel[discountAppliesTo] || "Discount applied";

  const [discountInput, setDiscountInput] = useState("");
  const [discountError, setDiscountError] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const discountInputRef = useRef<HTMLInputElement>(null);

  // Auto-apply the offer's discount code if one is set in sessionStorage.
  // We bypass the API validation thunk because the offer was already validated
  // on the info page — formArr.finance is never set, so the thunk always fails.
  const STANDARD_PLATE_PRICE_INC_VAT = PRICES.platesPair;
  const [autoDiscountCode, setAutoDiscountCode] = useState<string | null>(null);
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('nrCheckoutOffer');
      if (stored) {
        const offer = JSON.parse(stored);
        if (offer?.auto_discount_code && offer?.discount_type && offer?.discount_price != null) {
          const baseForPercent = offer.applies_to === 'transfer_fee'
            ? parseFloat(registrationDetail?.transfer_fee || '0')
            : offer.applies_to === 'transfer_service'
            ? ORDER_TRANSFER_FEE_STANDARD
            : STANDARD_PLATE_PRICE_INC_VAT;
          const discAmt = offer.discount_type === 'percent'
            ? (parseFloat(offer.discount_price) / 100) * baseForPercent
            : parseFloat(offer.discount_price) * 1.2;  // fixed price is ex-VAT
          setAutoDiscountCode(offer.auto_discount_code);
          if (discountAmount === 0) {
            dispatch(setAutoDiscount({
              code:      offer.auto_discount_code,
              amount:    parseFloat(discAmt.toFixed(2)),
              appliesTo: offer.applies_to || 'plates',
            }));
          }
        }
      }
    } catch (_) {}
  }, []);

  const [registrationNoVatPrice, setRegistrationNoVatPrice] = useState(0);
  const [registrationVatPrice, setRegistrationVatPrice] = useState(0);
  const [dotPrice, setDotPrice] = useState(0);
  const [dotNoVatPrice, setDotNoVatPrice] = useState(0);
  const [dotVatPrice, setDotVatPrice] = useState(0);

  // plate price
  const [platePrice, setPlatePrice] = useState(0);
  const [plateNoVatPrice, setPlateNoVatPrice] = useState(0);
  const [plateVatPrice, setPlateVatPrice] = useState(0);

  // transfer price
  const [transferPrice, setTransferPrice] = useState(0);
  const [transferNoVatPrice, setTransferNoVatPrice] = useState(0);
  const [transferVatPrice, setTransferVatPrice] = useState(0);

  // card fee
  const [cardFee, setCardFee] = useState(0);
  const [totalPriceIncCardFee, setTotalPriceIncCardFee] = useState(0);

  // total price
  const [totalVatPrice, setTotalVatPrice] = useState(0);
  const [totalNoVatPrice, setTotalNoVatPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [totalPriceWithFixtureAndVat, setTotalPriceFinal] = useState(0);

  // delivery price
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [deliveryNoVatPrice, setDeliveryNoVatPrice] = useState(0);
  const [deliveryVatPrice, setDeliveryVatPrice] = useState(0);
  const [deliveryPriceWording, setDeliveryPriceWording] = useState("");

  // others
  const [giftWrapPriceVat, setGiftWrapPriceVat] = useState(0);
  const [giftWrapPriceExVat, setGiftWrapPriceExVat] = useState(0);
  const [giftWrapPrice, setGiftWrapPrice] = useState(0);
  const [totalVat, setTotalVat] = useState(0);

  useEffect(() => {
    let _totalNoVatPrice = 0;
    let _totalVatPrice = 0;
    let _totalPrice = 0;
    let _cardFee = 0;
    let _deliveryPrice = 0;
    let _deliveryNoVatPrice = 0;
    let _deliveryVatPrice = 0;
    let _transferPrice = 0;
    let _transferVatPrice = 0;
    let _transferNoVatPrice = 0;
    let _giftWrapPriceVat = 0;
    let _giftWrapPriceExVat = 0;
    let _giftWrapPrice = 0;
    let _registrationVatPrice = 0;
    let _registrationNoVatPrice = 0;
    let _platePrice = 0;
    let _platesVatPrice = 0;
    let _platesNoVatPrice = 0;
    let _totalPriceIncCardFee = 0;
    let _subTotal = 0;
    let _totalVat = 0;
    let _totalPriceWithFixtureAndVat = 0;
    let _dotPrice = 0;
    let _dotNoVatPrice = 0;
    let _dotVatPrice = 0;

    // site_Pricing_Dvla
    if (registrationDetail?.sourcecode === "1") {
      _registrationVatPrice = parseFloat(registrationDetail.vat);
      setRegistrationNoVatPrice(parseFloat(registrationDetail.price));
      setDotPrice(parseFloat(registrationDetail.transfer_fee));
      setDotNoVatPrice(parseFloat(registrationDetail.transfer_fee));
      setRegistrationVatPrice(_registrationVatPrice);

      // ACRYLIC PLATES
      if (formArr && formArr.hasOwnProperty("acrylic_plate_total")) {
        _platePrice = parseFloat(formArr.acrylic_plate_total);
        setPlatePrice(_platePrice);
      } else {
        _platePrice = PRICES.platesPair;
        setPlatePrice(_platePrice);
      }
      _platesVatPrice = returnVatOfPrice(_platePrice);
      _platesNoVatPrice = returnPriceMinusVat(_platePrice);
      setPlateNoVatPrice(_platesNoVatPrice);
      setPlateVatPrice(_platesVatPrice);
      // card fee
      _cardFee = formArr?.CreditCardFee
        ? parseFloat(formArr?.CreditCardFee)
        : 0;
      setCardFee(_cardFee);

      // transfer service
      if (formArr?.transfer_type && formArr.transfer_type !== "none") {
        _transferPrice = ORDER_TRANSFER_FEE_STANDARD;
        _transferNoVatPrice = returnPriceMinusVat(ORDER_TRANSFER_FEE_STANDARD);
        _transferVatPrice = returnVatOfPrice(ORDER_TRANSFER_FEE_STANDARD);

        setTransferPrice(_transferPrice);
        setTransferNoVatPrice(_transferNoVatPrice);
        setTransferVatPrice(_transferVatPrice);
      }

      // delivery price
      if (formArr?.delivery_type) {
        if (formArr.delivery_type === "special") {
          _deliveryPrice = ORDER_SPECIAL_DELIVERY;
          _deliveryNoVatPrice = returnPriceMinusVat(_deliveryPrice);
          _deliveryVatPrice = returnVatOfPrice(_deliveryPrice);
          setDeliveryPriceWording("Special delivery");
        } else if (formArr.delivery_type === "recorded") {
          _deliveryPrice = ORDER_RECORDED_DELIVERY;
          _deliveryNoVatPrice = returnPriceMinusVat(_deliveryPrice);
          _deliveryVatPrice = returnVatOfPrice(_deliveryPrice);
          setDeliveryPriceWording("Recorded delivery");
        } else if (formArr.delivery_type === "standard") {
          const hasPlates = formArr?.order_plate === "yes";
          if (hasPlates) {
            _deliveryPrice = STANDARD_DELIVERY;
            _deliveryNoVatPrice = returnPriceMinusVat(_deliveryPrice);
            _deliveryVatPrice = returnVatOfPriceOnly(_deliveryNoVatPrice);
          } else {
            _deliveryPrice = 0;
            _deliveryNoVatPrice = 0;
            _deliveryVatPrice = 0;
          }
          setDeliveryPriceWording("Postage");
        }
      }

      setDeliveryPrice(_deliveryPrice);
      setDeliveryNoVatPrice(_deliveryNoVatPrice);
      setDeliveryVatPrice(_deliveryVatPrice);

      // total no vat price
      _totalNoVatPrice =
        parseFloat(registrationDetail.price) +
        parseFloat(registrationDetail.transfer_fee) +
        returnPriceMinusVat(_platePrice) +
        _deliveryNoVatPrice +
        _transferNoVatPrice;
      setTotalNoVatPrice(_totalNoVatPrice);

      // total vat price
      _totalVatPrice =
        parseFloat(registrationDetail.vat) +
        returnVatOfPrice(_platePrice) +
        _deliveryVatPrice +
        _transferVatPrice;
      setTotalVatPrice(_totalVatPrice);

      // total price
      setTotalPrice(_totalNoVatPrice + _totalVatPrice);

      // total price inc card fee
      _totalPriceIncCardFee = _totalNoVatPrice + _totalVatPrice + _cardFee;
      setTotalPriceIncCardFee(_totalPriceIncCardFee);

      // others
      if (formArr?.gift_wrapping === "yes") {
        _giftWrapPriceVat = 1.19;
        _giftWrapPriceExVat = 5.95;
        _giftWrapPrice = PRICES.giftWrap;
        setGiftWrapPriceVat(1.19);
        setGiftWrapPriceExVat(5.95);
        setGiftWrapPrice(PRICES.giftWrap);
      }

      // total vat
      _totalVat =
        _registrationVatPrice +
        _deliveryVatPrice +
        _platesVatPrice +
        _transferVatPrice +
        _giftWrapPriceVat;
      setTotalVat(_totalVat);
      //sub total
      _subTotal = _totalPriceIncCardFee - _totalVat;
      if (formArr?.gift_wrapping === "yes") {
        _subTotal = _subTotal + _giftWrapPrice;
      }
      setSubTotal(_subTotal);

      // total price with fixture and vat
      _totalPriceWithFixtureAndVat = _totalPriceIncCardFee + _giftWrapPrice;
      setTotalPriceFinal(_totalPriceWithFixtureAndVat);
    } else if (registrationDetail?.sourcecode === "134") {
      // calculate price when sourcecode is 134
      // site pricing stock

      // registration prices
      if (
        registrationDetail?.originalPrice &&
        registrationDetail.originalPrice !== registrationDetail.price
      ) {
        _registrationNoVatPrice = parseFloat(registrationDetail.originalPrice);
      } else {
        _registrationNoVatPrice = parseFloat(registrationDetail.price);
      }
      _registrationVatPrice = parseFloat(registrationDetail?.vat);

      setRegistrationNoVatPrice(_registrationNoVatPrice);
      setRegistrationVatPrice(_registrationVatPrice);

      // dot price
      _dotPrice = parseFloat(registrationDetail?.transfer_fee);
      _dotVatPrice = returnVatOfPrice(_dotPrice);
      _dotNoVatPrice = returnPriceMinusVat(_dotPrice);
      setDotPrice(_dotPrice);
      setDotVatPrice(_dotVatPrice);
      setDotNoVatPrice(_dotNoVatPrice);

      // acrylic plates
      _platePrice =
        formArr?.order_plate && formArr.order_plate === "yes"
          ? parseFloat(formArr.acrylic_plate_total)
          : 0;
      _platesNoVatPrice =
        _platePrice > 0 ? returnPriceMinusVat(_platePrice) : 0;
      _platesVatPrice = _platePrice > 0 ? returnVatOfPrice(_platePrice) : 0;
      setPlatePrice(_platePrice);
      setPlateNoVatPrice(_platesNoVatPrice);
      setPlateVatPrice(_platesVatPrice);

      // free postage
      _deliveryPrice = 0;
      _deliveryNoVatPrice = 0;
      _deliveryVatPrice = 0;
      setDeliveryPrice(_deliveryPrice);
      setDeliveryNoVatPrice(_deliveryNoVatPrice);
      setDeliveryVatPrice(_deliveryVatPrice);

      // gift wrap
      if (formArr?.gift_wrapping === "yes") {
        _giftWrapPriceVat = 1.19;
        _giftWrapPriceExVat = 5.95;
        _giftWrapPrice = PRICES.giftWrap;
      }
      setGiftWrapPriceExVat(_giftWrapPriceExVat);
      setGiftWrapPriceVat(_giftWrapPriceVat);
      setGiftWrapPrice(_giftWrapPrice);

      // total price
      _totalNoVatPrice =
        _registrationNoVatPrice +
        _platesNoVatPrice +
        _deliveryNoVatPrice +
        _dotNoVatPrice;
      _totalVatPrice =
        _registrationVatPrice +
        _platesVatPrice +
        _deliveryVatPrice +
        _dotVatPrice;
      _totalPrice = _totalNoVatPrice + _totalVatPrice;
      _totalPriceWithFixtureAndVat = _totalPrice;
      setTotalNoVatPrice(_totalNoVatPrice);
      setTotalVatPrice(_totalVatPrice);
      setTotalPrice(_totalPrice);
      setTotalPriceFinal(_totalPriceWithFixtureAndVat);
    } else if (registrationDetail) {
      // site_Pricing_Standard
      // registration prices
      if (
        registrationDetail.originalPrice &&
        registrationDetail.originalPrice !== registrationDetail.price
      ) {
        _registrationNoVatPrice = parseFloat(registrationDetail.originalPrice);
      } else {
        _registrationNoVatPrice = parseFloat(registrationDetail.price);
      }
      _registrationVatPrice = parseFloat(registrationDetail?.vat);
      setRegistrationNoVatPrice(_registrationNoVatPrice);
      setRegistrationVatPrice(_registrationVatPrice);

      // ACRYLIC PLATES
      _platePrice =
        formArr?.order_plate && formArr.order_plate === "yes"
          ? parseFloat(formArr.acrylic_plate_total)
          : 0;
      _platesNoVatPrice =
        _platePrice > 0 ? returnPriceMinusVat(_platePrice) : 0;
      _platesVatPrice = _platePrice > 0 ? returnVatOfPrice(_platePrice) : 0;
      setPlatePrice(_platePrice);
      setPlateNoVatPrice(_platesNoVatPrice);
      setPlateVatPrice(_platesVatPrice);

      // DVLA TRANSFER FEE
      _dotPrice = parseFloat(registrationDetail.transfer_fee);
      _dotNoVatPrice = parseFloat(registrationDetail.transfer_fee);
      _dotVatPrice = !registrationDetail.web_vat
        ? 0
        : returnVatOfPriceOnly(_dotPrice);
      setDotPrice(_dotPrice);
      setDotVatPrice(_dotVatPrice);
      setDotNoVatPrice(_dotNoVatPrice);

      // free postage
      _deliveryPrice = 0;
      _deliveryNoVatPrice = 0;
      _deliveryVatPrice = 0;
      setDeliveryPrice(_deliveryPrice);
      setDeliveryNoVatPrice(_deliveryNoVatPrice);
      setDeliveryVatPrice(_deliveryVatPrice);

      // gift wrap
      if (formArr?.gift_wrapping === "yes") {
        _giftWrapPriceVat = 1.19;
        _giftWrapPriceExVat = 5.95;
        _giftWrapPrice = PRICES.giftWrap;
      }
      setGiftWrapPriceExVat(_giftWrapPriceExVat);
      setGiftWrapPriceVat(_giftWrapPriceVat);
      setGiftWrapPrice(_giftWrapPrice);

      // total price
      _totalNoVatPrice =
        _registrationNoVatPrice +
        _platesNoVatPrice +
        _deliveryNoVatPrice +
        _dotNoVatPrice;
      _totalVatPrice =
        _registrationVatPrice +
        _platesVatPrice +
        _deliveryVatPrice +
        _dotVatPrice;
      _totalPrice = _totalNoVatPrice + _totalVatPrice;
      _totalPriceWithFixtureAndVat = _totalPrice;
      setTotalNoVatPrice(_totalNoVatPrice);
      setTotalVatPrice(_totalVatPrice);
      setTotalPrice(_totalPrice);
      setTotalPriceFinal(_totalPriceWithFixtureAndVat);

      // card fee
      _cardFee = formArr?.CreditCardFee ? parseFloat(formArr.CreditCardFee) : 0;
      _totalPriceIncCardFee = _totalNoVatPrice + _totalVatPrice + _cardFee;
      setCardFee(_cardFee);
      setTotalPriceIncCardFee(_totalPriceIncCardFee);
    }

    // old logic in jQuery, set total price to calc price
    setTotalPrice(calcPrice);
    setTotalPriceFinal(calcPrice);
  }, [registrationDetail]);

  useEffect(() => {
    plateFixtureValue === 0
      ? setTotalPriceFinal(totalPrice)
      : setTotalPriceFinal(Number((parseFloat(totalPrice.toString()) + plateFixtureValue).toFixed(2)));
  }, [plateFixtureValue]);

  useEffect(() => {
    if (isFinance && totalPriceWithFixtureAndVat > 0) {
      dispatch(setFinancePrice(parseFloat(Math.max(0, totalPriceWithFixtureAndVat - discountAmount).toFixed(2))));
    }
  }, [totalPriceWithFixtureAndVat, isFinance, discountAmount]);

  const handleApplyDiscount = async () => {
    const code = discountInput.trim().toUpperCase();
    if (!code) return;
    setDiscountError("");
    setDiscountLoading(true);
    const result = await dispatch(
      applyDiscountCode({ registration, discountCode: code })
    );
    setDiscountLoading(false);
    if (applyDiscountCode.rejected.match(result)) {
      setDiscountError((result.payload as string) || "Invalid discount code.");
    } else {
      setDiscountInput("");
    }
  };

  // When a plates discount is active, split it into ex-VAT and VAT components
  // so we can show strikethrough pricing inline on both plate rows
  const plateDiscountExVat = discountAppliesTo === 'plates' && discountAmount > 0
    ? parseFloat((discountAmount / 1.2).toFixed(2))
    : 0;
  const plateDiscountVatPart = discountAppliesTo === 'plates' && discountAmount > 0
    ? parseFloat((discountAmount - discountAmount / 1.2).toFixed(2))
    : 0;
  // transfer_fee discount → DVLA compulsory fee row; transfer_service discount → Transfer & administration row
  const dvlaFeeDiscount = discountAppliesTo === 'transfer_fee' && discountAmount > 0 ? discountAmount : 0;
  const transferServiceDiscount = discountAppliesTo === 'transfer_service' && discountAmount > 0 ? discountAmount : 0;

  return (
    <div className="p-6 bg-card-bg-grey rounded-2xl">
      <div className="">
        <p className="text-xl font-bold" style={{ color: "#358639" }}>
          Price Breakdown:
        </p>
        {registrationDetail?.sourcecode === "1" ? (
          <>
            {isFinance && (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Finance arrangement fee</span>
                <span className="">{POUND}0</span>
              </p>
            )}
            <p className="text-base font-bold flex align-center justify-between border-b py-4 px-1">
              <span>Registration Mark</span>
              <span className="">
                {POUND}
                {numberWithCommas(registrationNoVatPrice)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Compulsory DVLA transfer fee</span>
              <span className="flex items-center gap-2">
                {dvlaFeeDiscount > 0 && (
                  <span className="line-through text-gray-400">{POUND}{numberWithCommas(dotNoVatPrice)}</span>
                )}
                <span className={dvlaFeeDiscount > 0 ? "text-lime-600 font-semibold" : ""}>
                  {POUND}{numberWithCommas(parseFloat((dotNoVatPrice - dvlaFeeDiscount).toFixed(2)))}
                </span>
              </span>
            </p>
            {transferNoVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Transfer & administration</span>
                <span className="flex items-center gap-2">
                  {transferServiceDiscount > 0 && (
                    <span className="line-through text-gray-400">{POUND}{numberWithCommas(transferNoVatPrice)}</span>
                  )}
                  <span className={transferServiceDiscount > 0 ? "text-lime-600 font-semibold" : ""}>
                    {POUND}{numberWithCommas(parseFloat((transferNoVatPrice - transferServiceDiscount).toFixed(2)))}
                  </span>
                </span>
              </p>
            ) : (
              <></>
            )}
            {plateNoVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Acrylic plates</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(plateNoVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {deliveryPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>{deliveryPriceWording}</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(deliveryNoVatPrice)}
                </span>
              </p>
            ) : formArr?.order_plate !== "yes" ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Postage</span>
                <span className="font-bold" style={{ color: "#358639" }}>FREE</span>
              </p>
            ) : (
              <></>
            )}
            {formArr?.gift_wrapping === "yes" ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Gift Wrapping</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(giftWrapPriceExVat)}
                </span>
              </p>
            ) : (
              <></>
            )}
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Subtotal</span>
              <span className="">
                {POUND}
                {numberWithCommas(subTotal)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Total VAT @ 20%</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalVat)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Total Price</span>
              <span className="">
                {POUND}
                {/* according to old logic in jQuery, total price set to calc price*/}
                {numberWithCommas(totalPrice)}
                {/* {numberWithCommas(totalPriceIncCardFee + giftWrapPrice)} */}
              </span>
            </p>
            <p
              className={`text-base flex align-center justify-between border-b py-4 px-1 ${
                plateFixtureValue === 0 ? "hidden" : ""
              }`}
            >
              <span>Total Price ( including fixtures and VAT )</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPriceWithFixtureAndVat)}
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {registrationDetail?.sourcecode === "134" ? (
          <>
            {isFinance && (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Finance arrangement fee</span>
                <span className="">{POUND}0</span>
              </p>
            )}
            <p className="text-base font-bold flex align-center justify-between border-b py-4 px-1">
              <span>Registration Mark</span>
              <span className="">
                {POUND}
                {numberWithCommas(registrationNoVatPrice)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Compulsory DVLA transfer fee</span>
              <span className="flex items-center gap-2">
                {dvlaFeeDiscount > 0 && (
                  <span className="line-through text-gray-400">{POUND}{numberWithCommas(dotPrice)}</span>
                )}
                <span className={dvlaFeeDiscount > 0 ? "text-lime-600 font-semibold" : ""}>
                  {POUND}{numberWithCommas(parseFloat((dotPrice - dvlaFeeDiscount).toFixed(2)))}
                </span>
              </span>
            </p>
            {registrationVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>VAT @ 20%</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(registrationVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {plateNoVatPrice > 0 ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Standard plates</span>
                  <span className="flex items-center gap-2">
                    {plateDiscountExVat > 0 && (
                      <span className="line-through text-gray-400">{POUND}{numberWithCommas(plateNoVatPrice)}</span>
                    )}
                    <span className={plateDiscountExVat > 0 ? "text-lime-600 font-semibold" : ""}>
                      {POUND}{numberWithCommas(parseFloat((plateNoVatPrice - plateDiscountExVat).toFixed(2)))}
                    </span>
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on physical plates</span>
                  <span className="flex items-center gap-2">
                    {plateDiscountVatPart > 0 && (
                      <span className="line-through text-gray-400">{POUND}{numberWithCommas(plateVatPrice)}</span>
                    )}
                    <span className={plateDiscountVatPart > 0 ? "text-lime-600 font-semibold" : ""}>
                      {POUND}{numberWithCommas(parseFloat((plateVatPrice - plateDiscountVatPart).toFixed(2)))}
                    </span>
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            {formArr?.gift_wrapping === "yes" ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceExVat)}
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceVat)}
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Total Price</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPrice)}
              </span>
            </p>
            <p
              className={`text-base flex align-center justify-between border-b py-4 px-1 ${
                plateFixtureValue === 0 ? "hidden" : ""
              }`}
            >
              <span>Total Price ( including fixtures and VAT )</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPriceWithFixtureAndVat)}
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {registrationDetail?.sourcecode !== "134" &&
        registrationDetail?.sourcecode !== "1" ? (
          <>
            {isFinance && (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Finance arrangement fee</span>
                <span className="">{POUND}0</span>
              </p>
            )}
            <p className="text-base font-bold flex align-center justify-between border-b py-4 px-1">
              <span>Registration Mark</span>
              <span className="">
                {POUND}
                {numberWithCommas(registrationNoVatPrice)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Compulsory DVLA transfer fee</span>
              <span className="flex items-center gap-2">
                {dvlaFeeDiscount > 0 && (
                  <span className="line-through text-gray-400">{POUND}{numberWithCommas(dotPrice)}</span>
                )}
                <span className={dvlaFeeDiscount > 0 ? "text-lime-600 font-semibold" : ""}>
                  {POUND}{numberWithCommas(parseFloat((dotPrice - dvlaFeeDiscount).toFixed(2)))}
                </span>
              </span>
            </p>
            {registrationVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>VAT @ 20%</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(registrationVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {plateNoVatPrice > 0 ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Standard plates</span>
                  <span className="flex items-center gap-2">
                    {plateDiscountExVat > 0 && (
                      <span className="line-through text-gray-400">{POUND}{numberWithCommas(plateNoVatPrice)}</span>
                    )}
                    <span className={plateDiscountExVat > 0 ? "text-lime-600 font-semibold" : ""}>
                      {POUND}{numberWithCommas(parseFloat((plateNoVatPrice - plateDiscountExVat).toFixed(2)))}
                    </span>
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on physical plates</span>
                  <span className="flex items-center gap-2">
                    {plateDiscountVatPart > 0 && (
                      <span className="line-through text-gray-400">{POUND}{numberWithCommas(plateVatPrice)}</span>
                    )}
                    <span className={plateDiscountVatPart > 0 ? "text-lime-600 font-semibold" : ""}>
                      {POUND}{numberWithCommas(parseFloat((plateVatPrice - plateDiscountVatPart).toFixed(2)))}
                    </span>
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            {formArr?.gift_wrapping === "yes" ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceExVat)}
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceVat)}
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            <div className="text-base font-bold py-2 border-b">
              <p className="flex align-center justify-between py-2">
                <span>Total Price:</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(totalPrice)}
                </span>
              </p>
            </div>
            <p
              className={`text-base flex align-center justify-between border-b py-4 px-1 ${
                plateFixtureValue === 0 ? "hidden" : ""
              }`}
            >
              <span>Total Price ( including fixtures and VAT )</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPriceWithFixtureAndVat)}
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {!isFinance && (
          <p
            dangerouslySetInnerHTML={{ __html: creditCardMessage }}
            className="text-xs border-b py-4 px-1"
          ></p>
        )}
      </div>

      {/* Discount code */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        {discountAmount > 0 ? (
          <div>
            <p className="text-base flex align-center justify-between py-2 px-1 font-bold" style={{ color: "#358639" }}>
              <span>{appliedLabel} ({discountCode})</span>
              <span>-{POUND}{numberWithCommas(discountAmount)}</span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-2 px-1 font-bold">
              <span>Total after discount</span>
              <span>{POUND}{numberWithCommas(Math.max(0, totalPrice - discountAmount))}</span>
            </p>
            {discountCode !== autoDiscountCode && (
              <button
                onClick={() => dispatch(clearDiscount())}
                className="text-sm text-gray-400 underline mt-2"
              >
                Remove discount
              </button>
            )}
          </div>
        ) : !autoDiscountCode ? (
          <div>
            <p className="text-sm font-bold mb-2" style={{ color: "#358639" }}>Have a discount code?</p>
            <div className="flex gap-2">
              <input
                ref={discountInputRef}
                type="text"
                value={discountInput}
                onChange={(e) => {
                  setDiscountInput(e.target.value.toUpperCase());
                  setDiscountError("");
                }}
                onKeyDown={(e) => { if (e.key === "Enter") handleApplyDiscount(); }}
                placeholder="Enter code"
                disabled={discountLoading}
                className="border border-slate-300 rounded px-3 py-2 text-sm flex-1 uppercase tracking-widest focus:outline-none focus:border-green-600 bg-white"
              />
              <button
                onClick={handleApplyDiscount}
                disabled={!discountInput.trim() || discountLoading}
                className="bg-green-700 hover:bg-green-800 disabled:opacity-50 text-white rounded px-4 py-2 text-sm font-bold transition-colors"
              >
                {discountLoading ? "..." : "Apply"}
              </button>
            </div>
            {discountError && (
              <p className="text-red-500 text-sm mt-1">{discountError}</p>
            )}
          </div>
        ) : null}
      </div>

      <div className={`mt-4 ${formArr?.order_plate !== "no" ? "" : "hidden"}`}>
        <p className="text-base font-bold mb-4">
          Add fixtures for your plates:
        </p>
        <div className="grid lg:grid-cols-2 md:grid-cols-1 gap-8">
          <div
            className={`border border-solid border-slate-300 hover:border-lime-600 rounded cursor-pointer p-4 flex flex-col ${
              plateFixture === "screw-fixtures" ? "border-lime-600" : ""
            }`}
            style={{ backgroundColor: "#eeeeee" }}
            onClick={() => {
              if (plateFixture === "screw-fixtures") {
                dispatch(setPlateFixture(""));
                dispatch(setPlateFixtureValue(0));
              } else {
                dispatch(setPlateFixture("screw-fixtures"));
                dispatch(setPlateFixtureValue(2.95 * 1.2));
              }
            }}
          >
            <div className="">
              <p className="text-lg font-bold flex align-center justify-between">
                <span>Screw Fixtures</span>
                <span className="text-lime-600">{POUND}2.95</span>
              </p>
              <ul className="list-disc px-4 mt-4 text-sm">
                <li className="mt-2">Complete plate fixing set</li>
                <li className="mt-2">For direct surface mounting</li>
                <li className="mt-2">Coloured cap cover included</li>
              </ul>
            </div>

            <div className="w-full flex justify-center mt-auto pt-4">
              <div className="font-bold border border-solid border-lime-600 border-emerald-600 rounded text-center p-2 text-lime-600 w-10/12">
                Add This
              </div>
            </div>
          </div>
          <div
            className={`border border-solid border-slate-300 hover:border-lime-600 rounded cursor-pointer p-4 flex flex-col ${
              plateFixture === "pad-fixtures" ? "border-lime-600" : ""
            }`}
            style={{ backgroundColor: "#eeeeee" }}
            onClick={() => {
              if (plateFixture === "pad-fixtures") {
                dispatch(setPlateFixture(""));
                dispatch(setPlateFixtureValue(0));
              } else {
                dispatch(setPlateFixture("pad-fixtures"));
                dispatch(setPlateFixtureValue(4.95 * 1.2));
              }
            }}
          >
            <div className="">
              <p className="text-lg font-bold flex align-center justify-between">
                <span>Adhesive Pads</span>
                <span className="text-lime-600">{POUND}4.95</span>
              </p>
              <ul className="list-disc px-4 mt-4 text-sm">
                <li className="mt-2">Quick easy number plate fixings</li>
                <li className="mt-2">No drilling or screws required</li>
                <li className="mt-2">High adhesive for long lasting grip</li>
                <li className="mt-2">
                  Recommended for Acrylic & Plastic number plates
                </li>
                <li className="mt-2">Secure Fixing</li>
              </ul>
            </div>

            <div className="w-full flex justify-center mt-auto pt-4">
              <div className="font-bold border border-solid border-lime-600 border-emerald-600 rounded text-center p-2 text-lime-600 w-10/12">
                Add This
              </div>
            </div>
          </div>
        </div>
      </div>
      {formArr?.order_plate === "yes" &&
        registrationDetail?.sourcecode !== "134" &&
        registrationDetail?.sourcecode !== "1" && (
          <p className="text-xs py-1 px-1">
            We will also provide you with a new set of high quality acrylic
            plates displaying{" "}
            <strong>{registrationDetail?.registration}</strong> delivered
            directly to your door within 7 working days upon receipt of the
            requested identity documents. These will be manufactured in line
            with our{" "}
            <a
              target="_blank"
              href="https://www.newreg.co.uk/services/terms_and_conditions.html"
              rel="noreferrer"
              className="text-[#2FA1FF]"
            >
              Terms and Conditions
            </a>
            .
          </p>
        )}
    </div>
  );
};

export { PriceBreakdown, PriceBreakdown as default };
